import { create } from "zustand";

export type Theme = "light" | "dark";

/**
 * Each account's preference lives under `THEME_STORAGE_KEY:<account>`, and
 * `THEME_ACCOUNT_KEY` names the account last signed in on this browser. Keep in
 * sync with the inline script in index.html, which reads both before React
 * mounts so a refresh never flashes the wrong theme.
 */
export const THEME_STORAGE_KEY = "bridge-atlas-theme";
export const THEME_ACCOUNT_KEY = "bridge-atlas-theme-account";

const themeKey = (account: string) => `${THEME_STORAGE_KEY}:${account}`;

const readStoredAccount = (): string | null => {
  try {
    // The old browser-wide preference isn't tied to anyone — drop it so every
    // account starts in light mode.
    localStorage.removeItem(THEME_STORAGE_KEY);
    return localStorage.getItem(THEME_ACCOUNT_KEY);
  } catch {
    return null;
  }
};

/** Light unless this account has saved dark. */
const readStoredTheme = (account: string | null): Theme => {
  if (!account) return "light";
  try {
    return localStorage.getItem(themeKey(account)) === "dark" ? "dark" : "light";
  } catch {
    return "light";
  }
};

const applyTheme = (theme: Theme) => {
  document.documentElement.classList.toggle("dark", theme === "dark");
};

/**
 * Repaints with a circular reveal growing from the theme switch (falling back
 * to the top-right corner). Skipped without View Transitions support or when
 * the user prefers reduced motion.
 */
const applyThemeAnimated = () => {
  const applyCurrent = () => applyTheme(selectEffectiveTheme(useThemeStore.getState()));
  const doc = document as Document & {
    startViewTransition?: (cb: () => void) => { ready: Promise<void> };
  };
  if (!doc.startViewTransition || window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
    applyCurrent();
    return;
  }
  const rect = document.querySelector('[aria-label="Dark mode"]')?.getBoundingClientRect();
  const x = rect ? rect.left + rect.width / 2 : window.innerWidth - 40;
  const y = rect ? rect.top + rect.height / 2 : 30;
  const radius = Math.hypot(Math.max(x, window.innerWidth - x), Math.max(y, window.innerHeight - y));
  const transition = doc.startViewTransition(applyCurrent);
  transition.ready
    .then(() => {
      document.documentElement.animate(
        { clipPath: [`circle(0px at ${x}px ${y}px)`, `circle(${radius}px at ${x}px ${y}px)`] },
        { duration: 600, easing: "ease-in-out", pseudoElement: "::view-transition-new(root)" },
      );
    })
    .catch(() => {});
};

interface ThemeStore {
  /** The signed-in account the preference belongs to (its email). */
  account: string | null;
  /** That account's saved preference — light by default. */
  theme: Theme;
  /**
   * Forces light mode without touching the saved preference — set while a
   * page embeds content that has no dark mode (the Conversations iframe).
   */
  lightLocked: boolean;
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
  /** Switch to an account's saved theme; `null` resets to light. */
  setAccount: (account: string | null) => void;
  /**
   * Sign-out: forget whose theme to pre-paint so the next sign-in starts in
   * light mode, without repainting — the page stays as-is until it navigates away.
   */
  forgetAccount: () => void;
  setLightLocked: (locked: boolean) => void;
}

/** The theme actually painted: the preference, unless a page has locked it to light. */
export const selectEffectiveTheme = (s: Pick<ThemeStore, "theme" | "lightLocked">): Theme =>
  s.lightLocked ? "light" : s.theme;

/**
 * Light / dark preference. The theme is a `.dark` class on <html>; every colour
 * token flips through CSS variables (see tailwind.config.ts).
 */
const initialAccount = readStoredAccount();

export const useThemeStore = create<ThemeStore>((set, get) => ({
  account: initialAccount,
  theme: readStoredTheme(initialAccount),
  lightLocked: false,
  setTheme: (theme) => {
    const { account } = get();
    if (account) {
      try {
        localStorage.setItem(themeKey(account), theme);
      } catch {
        // Storage unavailable (private mode) — the choice lasts for this session.
      }
    }
    set({ theme });
    applyThemeAnimated();
  },
  toggleTheme: () => get().setTheme(get().theme === "dark" ? "light" : "dark"),
  setAccount: (rawAccount) => {
    const account = rawAccount?.trim().toLowerCase() || null;
    try {
      if (account) localStorage.setItem(THEME_ACCOUNT_KEY, account);
      else localStorage.removeItem(THEME_ACCOUNT_KEY);
    } catch {
      // Storage unavailable — fall through with the in-memory account.
    }
    set({ account, theme: readStoredTheme(account) });
    applyTheme(selectEffectiveTheme(get()));
  },
  forgetAccount: () => {
    try {
      localStorage.removeItem(THEME_ACCOUNT_KEY);
    } catch {
      // Storage unavailable — nothing was persisted to forget.
    }
  },
  setLightLocked: (lightLocked) => {
    set({ lightLocked });
    applyTheme(selectEffectiveTheme(get()));
  },
}));
